// The talent dashboard's glyphs: inline currentColor SVGs, the repo's icon convention (no
// icon library; web's own sets cannot be imported across apps). 24px grid, 1.7 stroke, sized
// by the caller through `size`. Decorative by default: every one sits beside a visible label.

import type { ReactNode } from "react";

type IconProps = { size?: number; className?: string };

function Glyph({ size = 18, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Mira's brand mark: the white "m" on the dark-green disc, wherever Mira herself is
 *  speaking or advising. The same geometry as web's `MiraMarkIcon` (apps cannot import each
 *  other). A filled logo with its own palette, so it carries explicit fills, not
 *  currentColor. */
export function MiraMarkIcon({ size = 26, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="24" cy="24" r="24" fill="#003912" />
      <path
        d="M14 31V17.3005H17.5419V20.6374L17.1619 20.0857C17.3654 19.0318 17.8533 18.2536 18.6298 17.7534C19.4062 17.2511 20.3343 17 21.4138 17C22.5599 17 23.5689 17.2882 24.4367 17.8666C25.3045 18.443 25.8484 19.217 26.0685 20.1866L24.9806 20.2875C25.4353 19.1676 26.0851 18.3401 26.928 17.8028C27.773 17.2676 28.7592 17 29.8907 17C30.8851 17 31.7675 17.2182 32.5336 17.6526C33.3017 18.0869 33.9038 18.6942 34.3419 19.4723C34.7799 20.2504 35 21.1582 35 22.1936V31H31.2048V22.9965C31.2048 22.4613 31.1073 22.0022 30.9142 21.6173C30.7211 21.2323 30.4471 20.9318 30.092 20.7136C29.737 20.4954 29.3073 20.3883 28.8007 20.3883C28.2941 20.3883 27.8851 20.4974 27.5239 20.7136C27.1606 20.9318 26.8824 21.2323 26.6893 21.6173C26.4941 22.0022 26.3986 22.4613 26.3986 22.9965V31H22.6035V22.9965C22.6035 22.4613 22.5059 22.0022 22.3128 21.6173C22.1176 21.2323 21.8457 20.9318 21.4907 20.7136C21.1356 20.4954 20.7059 20.3883 20.1993 20.3883C19.6927 20.3883 19.2837 20.4974 18.9225 20.7136C18.5592 20.9318 18.281 21.2323 18.0879 21.6173C17.8927 22.0022 17.7972 22.4613 17.7972 22.9965V31H14Z"
        fill="#fff"
      />
    </svg>
  );
}

export const ChevronLeftIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M15 6l-6 6 6 6" />
  </Glyph>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M9 6l6 6-6 6" />
  </Glyph>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M6 9l6 6 6-6" />
  </Glyph>
);

export const WalletIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18v3" />
    <rect x="4" y="8" width="16" height="11" rx="2.5" />
    <path d="M16 13.5h.01" />
  </Glyph>
);

export const ClockIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Glyph>
);

export const CalendarIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="4" y="5.5" width="16" height="14" rx="2.5" />
    <path d="M8 3.5v4M16 3.5v4M4 10h16" />
  </Glyph>
);

export const LayersIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 4l8.5 4.5L12 13 3.5 8.5z" />
    <path d="M3.5 12.5L12 17l8.5-4.5" />
  </Glyph>
);

export const UsersIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="9" cy="9" r="3.2" />
    <path d="M3.5 19c.6-3 2.8-4.8 5.5-4.8s4.9 1.8 5.5 4.8" />
    <path d="M15.5 6.2a3.2 3.2 0 0 1 0 5.6M17.5 14.6c1.6.7 2.6 2.2 3 4.4" />
  </Glyph>
);

export const SlidersIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M5 6.5h9M18 6.5h1M5 12h3M12 12h7M5 17.5h11M20 17.5h-1" />
    <circle cx="16" cy="6.5" r="2" />
    <circle cx="10" cy="12" r="2" />
    <circle cx="18" cy="17.5" r="2" />
  </Glyph>
);

export const CloseIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </Glyph>
);

export const CheckIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Glyph>
);

export const AlertIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 4.5l8.5 15h-17z" />
    <path d="M12 10v4M12 17h.01" />
  </Glyph>
);

export const SendIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M5 12h13M13 6.5l5.5 5.5-5.5 5.5" />
  </Glyph>
);

export const PaperclipIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M19 11.5l-6.8 6.8a4.2 4.2 0 0 1-6-6l7.2-7.2a2.8 2.8 0 0 1 4 4l-7.1 7.1a1.4 1.4 0 0 1-2-2l6.4-6.4" />
  </Glyph>
);

export const DocIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M7 3.5h7l4 4v13H7z" />
    <path d="M14 3.5v4h4M9.5 12.5h5M9.5 16h5" />
  </Glyph>
);

export const ArrowUpRightIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M7.5 16.5l9-9M9 7.5h7.5V15" />
  </Glyph>
);

export const InboxIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 13.5l2.2-7A2 2 0 0 1 8.1 5h7.8a2 2 0 0 1 1.9 1.5l2.2 7" />
    <path d="M4 13.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3.5h-4.5a3.5 3.5 0 0 1-7 0z" />
  </Glyph>
);

export const PlusIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 5.5v13M5.5 12h13" />
  </Glyph>
);

/** "Where this project stands": three rising bars. */
export const BarsIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M6 19v-5M12 19V9M18 19V5" />
    <path d="M4 19.5h16" />
  </Glyph>
);

/** The project's timer: sand running down, so it never reads as the Timeline fact's clock. */
export const HourglassIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M7 4.5h10M7 19.5h10" />
    <path d="M8 4.5c0 4 4 5.2 4 7.5s-4 3.5-4 7.5M16 4.5c0 4-4 5.2-4 7.5s4 3.5 4 7.5" />
  </Glyph>
);
